import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { useSelector } from '../hook-utils';
import { CallState, Media, QueueSquadType, QueueOrSquadId, canRetrieveCall } from '../model';
import { actions } from '../store';
import { ColumnWidths, getGridSortOrder, selectGridColumnWidths, selectShowQueuingMedia } from '../store/reducers/localSettings';
import { selectMediaIsLoading } from '../store/reducers/media';
import { selectQueueMedia, selectExtension } from '../store/reducers/queueAgents';
import { getCallStatus } from './CallPopup';
import { useCompactView } from './compactView';
import { FlexTable, Resizing, SortRules, TableColumn } from './FlexTable';
import { DraggableMediaItem } from './MediaItem';
import { MediaMenuState } from './MediaMenu';
import './MediaTable.scss';
import { targetFromEvent } from './Menu';
import MoreButton from './MoreButton';
import Timer from './Timer';
import { ReactComponent as RetrieveIcon } from './icons/callcontrol/Retrieve.svg';
import { selectMyself } from '../store/reducers/config';
import { CellProps } from 'react-table';

type OnMenuState = { e: React.MouseEvent, item: Media }

function getColumns(onMore: (state: OnMenuState) => void, queue: QueueOrSquadId, columnsWidths?: ColumnWidths): Array<TableColumn<Media>> {
  const columns: Array<TableColumn<Media>> = [{
    id: 'icon',
    Header: '',
    width: 42,
    disableResizing: true,
    className: 'presence',
    accessor: i => { return <MediaContainer item={i} onMenu={e => onMore({ e, item: i })} /> },
  }, {
    id: 'caller',
    Header: 'Caller',
    accessor: i => clidDescription(i),
  }, {
    id: 'subject', Header: 'Subject / [Queue]',
    accessor: i => { return <MediaSubjectQueue item={i} queue={queue} /> },
  }, {
    id: 'agent',
    Header: 'Agent',
    accessor: i => i.extensionId ? `${i.fullName} (${i.extensionId})` : null,
  }, {
    id: 'status',
    Header: 'Status',
    accessor: i => getCallStatus(i.status) + (i.status === CallState.QueueHold && i.preferredExtnDescription ? ' for ' + i.preferredExtnDescription : ''),
  }, {
    id: 'time',
    Header: 'Duration',
    accessor: i => i.elapsedTime,
    Cell: (cell: CellProps<Media, number>) => <Timer ts={cell.cell.value} />,
  }, {
    id: 'retrieveButton',
    accessor: i => <RetrieveButton className='menu-button' item={i} />,
    width: 35,
    disableResizing: true,
    disableSortBy: true,
  }, {
    id: 'actions',
    accessor: i => <MoreButton className='menu-button' onClick={e => onMore({ e, item: i })} />,
    width: 35,
    disableResizing: true,
    disableSortBy: true,
  }]

  return columns.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

export default function MediaTable({ id, server, type, onMediaMenu, maxTableHeight }: { id: string, server: string, type: QueueSquadType, onMediaMenu: (state: MediaMenuState) => void, maxTableHeight?: number }) {
  const dispatch = useDispatch()
  const gridType = 'media'
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()
  const sorted = useSelector(s => getGridSortOrder(s, gridType))

  const queue = useMemo((): QueueOrSquadId => ({ id, server, type }), [id, server, type])
  const media = useSelector(state => selectQueueMedia(state, queue))
  const isLoading = useSelector(state => selectMediaIsLoading(state, queue))
  const compactView = useCompactView()
  const showMedia = useSelector(s => selectShowQueuingMedia(s, queue))
  const [selected, setSelected] = useState<Media[]>([])
  const [sift, setSift] = useState(1)
  const [sift1, setSift1] = useState<Media>()
  const [sift2, setSift2] = useState<Media>()

  const sortedRows = useRef<Media[]>([])
  const onData = useCallback((data: Media[]) => sortedRows.current = data, [])

  useEffect(() => {
    dispatch(actions.fetchQueueMedia.request({ server, id, type }))
  }, [dispatch, server, id, type])

  useEffect(() => {
    if (!sortedRows || !sortedRows.current || !sift1 || !sift2) return

    const index1 = sortedRows.current.findIndex(m => m.id === sift1.id)
    const index2 = sortedRows.current.findIndex(m => m.id === sift2.id)
    const start = index1 < index2 ? index1 : index2
    const end = (index1 > index2 ? index1 : index2) + 1
    const filter = sortedRows.current.slice(start, end)
    setSelected(filter)
    setSift2(undefined)

  }, [sortedRows, sift1, sift2])

  const addSelectedItem = useCallback((item: Media, selected: Media[]) => {
    if (selected.length === 0 || (selected.length > 0 && selected.findIndex(f => f.id === item.id && f.server === item.server) === -1)) {
      return [...selected, item]
    }
    return selected
  }, [])

  const getRowProps = useCallback((data: Media): React.HTMLAttributes<HTMLTableRowElement> => ({
    className: (selected.length > 0 && selected.findIndex(f => f.id === data.id && f.server === data.server) !== -1) ? 'selected' : undefined,
    onClick(e) {
      if (e.shiftKey) {
        if (sift === 1) {
          setSift1(data)
          setSift(2)
          setSelected([data])
          dispatch(actions.setSelectedNumber(data.id))
        }

        if (sift === 2) {
          setSift2(data)
        }
      } else if (e.ctrlKey) {
        setSelected(addSelectedItem(data, selected))

        if (selected.length === 0) {
          dispatch(actions.setSelectedNumber(data.id))
        } else {
          dispatch(actions.clearSelectedNumber())
        }

      } else {
        setSift1(data)
        setSift(2)
        setSift2(undefined)
        setSelected([data])
        dispatch(actions.setSelectedNumber(data.id))
      }
    },
    onContextMenu(e: React.MouseEvent) {
      let d = [data]
      if (e.shiftKey && sift1) {
        const index1 = media.findIndex(m => m.id === sift1.id)
        const index2 = media.findIndex(m => m.id === data.id)
        d = media.splice(index1 < index2 ? index1 : index2, index1 > index2 ? (index1 - index2) + 1 : (index2 - index1) + 1)
      } else if (e.ctrlKey) {
        d = addSelectedItem(data, selected)
      }

      setSelected(d)
      onMediaMenu({ target: targetFromEvent(e), items: d })
    },
  }), [addSelectedItem, dispatch, media, onMediaMenu, selected, sift, sift1])

  const columns = useMemo(() => getColumns(function onMore(state: OnMenuState) {
    let d = [state.item]
    if (state.e.shiftKey && sift1) {
      const index1 = media.findIndex(m => m.id === sift1.id)
      const index2 = media.findIndex(m => m.id === state.item.id)
      d = media.splice(index1 < index2 ? index1 : index2, index1 > index2 ? (index1 - index2) + 1 : (index2 - index1) + 1)
    } else if (state.e.ctrlKey) {
      d = addSelectedItem(state.item, selected)
    }

    setSelected(d)
    onMediaMenu({ target: targetFromEvent(state.e), items: d })
  }, queue, widths), [addSelectedItem, media, onMediaMenu, queue, selected, sift1, widths])

  const onSortBy = useCallback((sortBy: SortRules<Media>) => {
    dispatch(actions.requestSetGridSortOrder(gridType, sortBy))
  }, [dispatch])

  const onColumnResize = useCallback((columnResize: Resizing) => {
    if (columnResize && columnResize.columnWidths) {
      for (const key in columnResize.columnWidths) {
        if (Object.prototype.hasOwnProperty.call(columnResize.columnWidths, key)) {
          if (refColumnResizeState.current) {
            refColumnResizeState.current = { ...refColumnResizeState.current, [key]: columnResize.columnWidths[key] }
          } else {
            refColumnResizeState.current = { [key]: columnResize.columnWidths[key] }
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  const table = useMemo(() => {
    return <FlexTable className={classNames('MediaTable', compactView && 'compact-view', !maxTableHeight || maxTableHeight > 1 && 'fixed')}
      data={media}
      loading={isLoading}
      columns={columns}
      getRowProps={getRowProps}
      sortBy={sorted}
      onSortBy={onSortBy}
      onColumnResize={onColumnResize}
      maxTableHeight={maxTableHeight}
      onData={onData}
    />
  },
    [columns, compactView, getRowProps, isLoading, maxTableHeight, media, onColumnResize, onData, onSortBy, sorted])

  if (!showMedia) { return null }

  return <>{table}</>
}

function MediaContainer({ item, onMenu }: { item: Media, onMenu: (e: React.MouseEvent, item: Media) => void }) {
  return <Droppable droppableId={item.id} isDropDisabled>
    {(provided) => <div ref={provided.innerRef} {...provided.droppableProps}>
      <DraggableMediaItem item={item} index={0} key={item.id} className='MediaItemCall' onMenu={(e) => onMenu(e, item)} />
      {provided.placeholder}
    </div>}
  </Droppable>
}

/*
function dnisDescription(call: Media): string {
  let value = '';
  switch (call.mediaType) {
    case 'CALL':
      value = call.dnisDescription
      break
    case 'VM':
      value = call.dnisDescription
      break
    case 'EMAIL':
      value = call.subject
      break
    case 'TWEET':
    case 'TC':
      value = call.subject.replace(/\r\n/g, '').replace(/\n\n/g, '').replace(/\n/g, '');
      break
  }
  return value
}
*/

function MediaSubjectQueue({ item, queue }: { item: Media, queue: QueueOrSquadId }) {
  if (queue.type === 'squad') {
    return <>{item.subject.replace(/\r\n/g, '').replace(/\n\n/g, '').replace(/\n/g, '') + ' [' + item.dnisDescription + ']'}</>
  }
  else if (item.dnis !== queue.id) {
    return <>{item.subject.replace(/\r\n/g, '').replace(/\n\n/g, '').replace(/\n/g, '') + ' [' + item.dnisDescription + ']'}</>
  }
  return <>{item.subject.replace(/\r\n/g, '').replace(/\n\n/g, '').replace(/\n/g, '')}</>
}

function clidDescription(call: Media): string {
  let value = call.clidDescription;
  switch (call.mediaType) {
    case 'CALL':
      if (value === null || value === '') {
        if (call.clid !== '') {
          value = call.clid;
        } else {
          value = 'Unknown Caller';
        }
      } else {
        if (call.clidAccountCode !== '') {
          value += ' a/c: ' + call.clidAccountCode
        }
      }

      break;
    case 'EMAIL':
      if (value === null || value === '') {
        value = call.clid;
      }
      break;
    case 'TC':
      break;
  }
  return value;
}

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  item: Media
}

export function RetrieveButton({ item, className, ...props }: Props) {
  const dispatch = useDispatch()
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))

  if (!extension || !myId) return null

  const retrieveStatus = canRetrieveCall([item], extension.status)

  if (!retrieveStatus) return null

  return <button className={classNames('MoreButton', className)} {...props} onClick={() => {
    if (retrieveStatus) {
      dispatch(actions.ccQueuePickup(myId.id, [item]))
    }
  }} >
    <RetrieveIcon className={'icon'} />
  </button>
}
